import { Link } from "react-router-dom";
import { Wallet, ArrowRight, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useCurrency } from "@/context/CurrencyContext";

/**
 * CheapestCallout — top-of-results horizontal strip surfacing the 5
 * cheapest degrees that are still Likely / Target / Ambitious for the
 * applicant. Uses the backend's `true_cost` payload (tuition +
 * mandatory semester fee + accommodation + food) so the "$0 tuition in
 * Germany doesn't mean free" story reaches the user before they even
 * start expanding countries.
 *
 * Rendered above the country summary in AdmissionCalculator. Bandless
 * long-shots are filtered server-side.
 */

const BAND_CLS = {
  likely:            "bg-emerald-50 text-emerald-800 border-emerald-200",
  target:            "bg-sky-50 text-sky-800 border-sky-200",
  ambitious:         "bg-amber-50 text-amber-800 border-amber-200",
  highly_ambitious:  "bg-stone-50 text-stone-700 border-stone-200",
};

const BAND_LABEL = {
  likely: "Likely",
  target: "Target",
  ambitious: "Ambitious",
  highly_ambitious: "Long-shot",
};

// Rough currency-symbol → flag-emoji lookup based on country name. Kept
// tiny on purpose: 20-ish flags is enough for the countries our catalog
// actually surfaces at the cheapest end (Germany, India, Argentina,
// Mexico, France…).  Missing countries just render without a flag.
const COUNTRY_FLAG = {
  Germany: "🇩🇪",
  France: "🇫🇷",
  Italy: "🇮🇹",
  Spain: "🇪🇸",
  Netherlands: "🇳🇱",
  Belgium: "🇧🇪",
  Ireland: "🇮🇪",
  Portugal: "🇵🇹",
  Poland: "🇵🇱",
  Austria: "🇦🇹",
  Switzerland: "🇨🇭",
  "United Kingdom": "🇬🇧",
  UK: "🇬🇧",
  USA: "🇺🇸",
  "United States": "🇺🇸",
  Canada: "🇨🇦",
  Australia: "🇦🇺",
  "New Zealand": "🇳🇿",
  India: "🇮🇳",
  Singapore: "🇸🇬",
  "Hong Kong": "🇭🇰",
  Japan: "🇯🇵",
  "South Korea": "🇰🇷",
  China: "🇨🇳",
  Argentina: "🇦🇷",
  Mexico: "🇲🇽",
  Brazil: "🇧🇷",
  UAE: "🇦🇪",
  "Saudi Arabia": "🇸🇦",
  Sweden: "🇸🇪",
  Denmark: "🇩🇰",
  Norway: "🇳🇴",
  Finland: "🇫🇮",
  Malaysia: "🇲🇾",
  Turkey: "🇹🇷",
  "South Africa": "🇿🇦",
};

// Region groupings for the top-of-callout filter pills. "UK" is the
// default (per Feb-2026 product decision — most WUA users are UK-bound).
// Regions are disjoint (so counts add up) except "All" which is a full
// pass-through. Order below is the render order in the pill row.
const UK_SET = new Set(["United Kingdom", "UK"]);
const EUROPE_SET = new Set([
  "Germany", "France", "Italy", "Spain", "Netherlands", "Belgium",
  "Ireland", "Portugal", "Poland", "Austria", "Switzerland", "Sweden",
  "Denmark", "Norway", "Finland", "Czech Republic", "Czechia", "Greece",
  "Hungary", "Slovakia", "Slovenia", "Estonia", "Latvia", "Lithuania",
  "Luxembourg", "Croatia", "Romania", "Bulgaria", "Iceland", "Cyprus",
  "Malta",
]);
const NORTH_AMERICA_SET = new Set([
  "US", "USA", "United States", "United States of America", "Canada",
]);
const ASIA_SET = new Set([
  "India", "China", "Japan", "South Korea", "Singapore", "Hong Kong",
  "Taiwan", "Malaysia", "Thailand", "Vietnam", "Indonesia", "Philippines",
  "Pakistan", "Bangladesh", "Sri Lanka", "Nepal",
]);
const OCEANIA_SET = new Set(["Australia", "New Zealand"]);
const MIDDLE_EAST_SET = new Set([
  "UAE", "United Arab Emirates", "Israel", "Saudi Arabia", "Qatar",
  "Kuwait", "Bahrain", "Oman", "Jordan", "Lebanon", "Turkey",
]);
const AFRICA_SET = new Set([
  "South Africa", "Egypt", "Morocco", "Kenya", "Nigeria", "Ghana",
  "Ethiopia", "Tanzania", "Uganda", "Tunisia", "Algeria",
]);
const LATAM_SET = new Set([
  "Argentina", "Brazil", "Mexico", "Chile", "Colombia", "Peru", "Uruguay",
  "Ecuador", "Costa Rica", "Panama", "Venezuela",
]);

const REGIONS = [
  { key: "UK",           label: "UK",           match: (c) => UK_SET.has(c) },
  { key: "Europe",       label: "Europe",       match: (c) => EUROPE_SET.has(c) },
  { key: "NorthAmerica", label: "N. America",   match: (c) => NORTH_AMERICA_SET.has(c) },
  { key: "Asia",         label: "Asia",         match: (c) => ASIA_SET.has(c) },
  { key: "Oceania",      label: "Oceania",      match: (c) => OCEANIA_SET.has(c) },
  { key: "MiddleEast",   label: "Middle East",  match: (c) => MIDDLE_EAST_SET.has(c) },
  { key: "Africa",       label: "Africa",       match: (c) => AFRICA_SET.has(c) },
  { key: "LatAm",        label: "Latin America", match: (c) => LATAM_SET.has(c) },
  { key: "All",          label: "All",           match: () => true },
];

export default function CheapestCallout({ items = [], loading = false, intendedDegree = "" }) {
  const { format } = useCurrency();
  const [region, setRegion] = useState("UK"); // default per product decision
  // Collapsed by default (owner ask, Feb 2026) — a family running a
  // Competitive-first search doesn't want a Cheapest-5 callout stealing
  // vertical real estate above the buckets. One click to reveal.
  const [open, setOpen] = useState(false);

  // Filter incoming items by selected region, then take top 5 by cost.
  // Items are already cost-sorted server-side so we preserve their order.
  const filteredItems = useMemo(() => {
    const rule = REGIONS.find((r) => r.key === region) || REGIONS[REGIONS.length - 1];
    return (items || []).filter((it) => rule.match(it.country)).slice(0, 5);
  }, [items, region]);

  // Which region pills actually have at least one match, so we can grey
  // out impossible-to-select pills (rather than let the user tap into a
  // dead-end empty state).
  const regionCounts = useMemo(() => {
    const counts = {};
    for (const r of REGIONS) {
      counts[r.key] = (items || []).filter((it) => r.match(it.country)).length;
    }
    return counts;
  }, [items]);

  if (loading) {
    return (
      <div
        data-testid="cheapest-callout-loading"
        className="mt-4 rounded-lg border border-stone-200 bg-white px-4 py-3 text-xs text-stone-500 font-underground-sc tracking-wider inline-flex items-center gap-2"
      >
        <Sparkles className="h-3 w-3 animate-pulse" />
        Ranking the cheapest options…
      </div>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <section
      data-testid="cheapest-callout"
      className="mt-4 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 via-white to-white px-5 py-4"
    >
      <button
        type="button"
        data-testid="cheapest-callout-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left"
      >
        <header className={`flex items-center justify-between gap-3 flex-wrap ${open ? "mb-3" : ""}`}>
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-emerald-700" />
            <div className="font-editorial text-base leading-tight">
              Cheapest {filteredItems.length} in your buckets
            </div>
            {/* Collapse-state affordance — carat plus a subtle prompt
                so a family who's ignored the callout doesn't miss that
                it's here for the wanting. */}
            <span className="text-[10px] font-underground-sc tracking-wider text-emerald-700 flex items-center gap-1">
              {open ? "hide" : "show"}
              <span aria-hidden>{open ? "▴" : "▾"}</span>
            </span>
          </div>
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 hidden sm:block">
            true annual cost · tuition + fees + housing + food
          </div>
        </header>
      </button>
      {open && (<>
      {/* Regional pill filter — UK first per product decision (Feb 2026) */}
      <div
        role="tablist"
        aria-label="Filter by region"
        className="flex items-center gap-1.5 mb-3 flex-wrap"
        data-testid="cheapest-callout-region-pills"
      >
        {REGIONS.map((r) => {
          const active = r.key === region;
          const count = regionCounts[r.key] || 0;
          // Hide zero-count pills entirely (keeps row uncluttered on
          // datasets that don't yet have any Latin-America / Africa /
          // Middle-East entries). "All" is always shown.
          if (count === 0 && r.key !== "All") return null;
          return (
            <button
              key={r.key}
              role="tab"
              aria-selected={active}
              onClick={() => setRegion(r.key)}
              data-testid={`cheapest-callout-region-${r.key.toLowerCase()}`}
              className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? "bg-emerald-700 text-white border-emerald-700"
                  : "bg-white text-stone-600 border-stone-200 hover:border-emerald-400 hover:text-emerald-800"
              }`}
            >
              {r.label}
              <span className={`ml-1 text-[9px] ${active ? "text-white/70" : "text-stone-400"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {filteredItems.length === 0 ? (
        <div
          data-testid="cheapest-callout-empty"
          className="rounded-lg border border-dashed border-stone-200 bg-white/70 px-4 py-6 text-center text-sm text-stone-500"
        >
          No matches in <span className="font-semibold text-stone-700">{region}</span> for your buckets yet.
          <button
            onClick={() => setRegion("All")}
            className="ml-2 text-[#4C1D95] hover:underline"
            data-testid="cheapest-callout-empty-see-all"
          >
            See all regions →
          </button>
        </div>
      ) : (
      <ol className="space-y-1.5">
        {filteredItems.map((it, idx) => {
          const flag = COUNTRY_FLAG[it.country] || "";
          const band = it.classification?.bucket || "target";
          const bandCls = BAND_CLS[band] || BAND_CLS.target;
          const bandLabel = BAND_LABEL[band] || band;
          const total = it.true_cost?.total_usd || 0;
          const feeHint =
            it.mandatory_semester_fee_usd != null
              ? it.eu_only_free
                ? "EU-free · non-EU pays"
                : "Tuition free · fees still due"
              : null;
          return (
            <li
              key={it.degree_id || it.university_id}
              data-testid={`cheapest-item-${idx}`}
              className="flex items-center justify-between gap-3 rounded-md border border-stone-100 bg-white/70 px-3 py-2 hover:bg-white hover:border-stone-200 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span
                  className="font-mono text-[11px] text-stone-500 w-4 text-right shrink-0"
                  aria-hidden
                >
                  {idx + 1}
                </span>
                {flag && (
                  <span className="text-base leading-none shrink-0" aria-hidden>
                    {flag}
                  </span>
                )}
                <div className="min-w-0">
                  <div className="text-sm leading-tight truncate">
                    {it.degree_id ? (
                      <Link
                        to={`/degree/${it.degree_id}`}
                        className="hover:underline underline-offset-4"
                      >
                        <span className="font-medium">{it.university_name}</span>
                        {it.degree_name && (
                          <span className="text-stone-500"> · {it.degree_name}</span>
                        )}
                      </Link>
                    ) : (
                      <span className="font-medium">{it.university_name}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap text-[10px] font-underground-sc tracking-wider text-stone-500">
                    <span>{it.country}</span>
                    <span
                      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 normal-case ${bandCls}`}
                    >
                      {bandLabel}
                    </span>
                    {feeHint && (
                      <span
                        title={it.semester_fee_covers || ""}
                        className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-1.5 py-0.5 normal-case"
                      >
                        {feeHint}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-semibold text-stone-900 tabular-nums">
                  {format(Math.round(total))}
                </div>
                <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                  /year all-in
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      )}

      <footer className="mt-3 flex items-center justify-between text-[11px] text-stone-500">
        <span>
          {intendedDegree ? (
            <>Ranked from your {intendedDegree} matches. Includes housing + food.</>
          ) : (
            <>Ranked from your matched degrees. Includes housing + food.</>
          )}
        </span>
        <Link
          to="/advanced"
          data-testid="cheapest-callout-advanced-link"
          className="hidden sm:inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
        >
          Compare against your budget
          <ArrowRight className="h-3 w-3" />
        </Link>
      </footer>
      </>)}
    </section>
  );
}
